import { useEffect, useState } from 'react';
import { shouldLockMobileViewportScale } from '../components/layout/routeChrome';
import { isInStandaloneMode, isIosDevice } from './pwaPlatform';

/** Present on `<html>` while play / match / event routes lock the visual viewport. */
export const VIEWPORT_SCALE_LOCK_ATTR = 'data-lock-viewport-scale';

/** Holds the WebKit prefixes Chakra's style system drops. */
export const VIEWPORT_SCALE_LOCK_STYLE_ID = 'riftbound-viewport-scale-lock';

const VIEWPORT_SCALE_LOCK_CSS = `
html[${VIEWPORT_SCALE_LOCK_ATTR}],
html[${VIEWPORT_SCALE_LOCK_ATTR}] body,
html[${VIEWPORT_SCALE_LOCK_ATTR}] body * {
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
html[${VIEWPORT_SCALE_LOCK_ATTR}] body :is(input, textarea, code, [contenteditable="true"]) {
  -webkit-user-select: text;
  -webkit-touch-callout: default;
}
`;

function ensureViewportLockStyles(): void {
  if (document.getElementById(VIEWPORT_SCALE_LOCK_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = VIEWPORT_SCALE_LOCK_STYLE_ID;
  style.textContent = VIEWPORT_SCALE_LOCK_CSS;
  document.head.appendChild(style);
}

/**
 * Forces scale back to 1. `maximum-scale` is honored by Android browsers; iOS
 * often ignores it, so the touch-action rules and gesture listeners do the
 * preventing, and rewriting this tag is how a stuck scale gets dropped.
 */
export const LOCKED_VIEWPORT_CONTENT =
  'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

/** Intermediate value so WebKit observes a viewport change and recomputes scale. */
export const OPEN_VIEWPORT_CONTENT =
  'width=device-width, initial-scale=1.0, viewport-fit=cover';

const SNAP_DELAY_MS = 50;
const MAX_SNAP_ATTEMPTS = 2;

const DEV_PHONE_LOCK_KEY = 'riftbound-dev-phone-lock';
const DEV_PHONE_LOCK_EVENT = 'riftbound-dev-phone-lock';

export type DevPhoneLockOverride = 'auto' | 'on' | 'off';

export function readDevPhoneLockOverride(): DevPhoneLockOverride {
  if (!import.meta.env.DEV || typeof sessionStorage === 'undefined') return 'auto';
  try {
    const value = sessionStorage.getItem(DEV_PHONE_LOCK_KEY);
    if (value === 'on' || value === 'off') return value;
  } catch {
    // sessionStorage can throw in private mode.
  }
  return 'auto';
}

export function writeDevPhoneLockOverride(value: DevPhoneLockOverride): void {
  if (!import.meta.env.DEV || typeof sessionStorage === 'undefined') return;
  try {
    if (value === 'auto') sessionStorage.removeItem(DEV_PHONE_LOCK_KEY);
    else sessionStorage.setItem(DEV_PHONE_LOCK_KEY, value);
  } catch {
    // Ignore storage failures; the in-memory event still updates this tab.
  }
  window.dispatchEvent(new Event(DEV_PHONE_LOCK_EVENT));
}

function useDevPhoneLockOverride(): DevPhoneLockOverride {
  const [override, setOverride] = useState(readDevPhoneLockOverride);
  useEffect(() => {
    const sync = () => setOverride(readDevPhoneLockOverride());
    window.addEventListener(DEV_PHONE_LOCK_EVENT, sync);
    return () => window.removeEventListener(DEV_PHONE_LOCK_EVENT, sync);
  }, []);
  return override;
}

export interface PhoneGestureSnapshot {
  pathname: string;
  routeLocksOnPhone: boolean;
  override: DevPhoneLockOverride;
  ios: boolean;
  coarsePointer: boolean;
  standalone: boolean;
  applied: boolean;
  headingSelect: string | null;
  fieldSelect: string | null;
  fieldLabel: string | null;
  scale: number;
  viewportLocked: boolean;
}

export function readPhoneGestureSnapshot(pathname: string): PhoneGestureSnapshot {
  const heading = document.querySelector('h1');
  const field = document.querySelector(
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]), textarea',
  );
  const coarsePointer =
    typeof window.matchMedia === 'function' && window.matchMedia('(any-pointer: coarse)').matches;
  const viewport = document.querySelector('meta[name="viewport"]')?.getAttribute('content') ?? '';
  return {
    pathname,
    routeLocksOnPhone: shouldLockMobileViewportScale(pathname),
    override: readDevPhoneLockOverride(),
    ios: isIosDevice(),
    coarsePointer,
    standalone: isInStandaloneMode(),
    applied: document.documentElement.hasAttribute(VIEWPORT_SCALE_LOCK_ATTR),
    headingSelect: heading ? getComputedStyle(heading).userSelect : null,
    fieldSelect: field instanceof Element ? getComputedStyle(field).userSelect : null,
    fieldLabel: field?.getAttribute('placeholder') || field?.getAttribute('aria-label') || null,
    scale: window.visualViewport?.scale ?? 1,
    viewportLocked: viewport.includes('maximum-scale=1'),
  };
}

export function deviceAllowsViewportScaleLock(
  matchMedia: ((query: string) => { matches: boolean }) | undefined,
  nav: Navigator,
): boolean {
  if (isIosDevice(nav)) return true;
  if (typeof matchMedia !== 'function') return false;
  return matchMedia('(any-pointer: coarse)').matches;
}

function isTextField(el: Element | null): boolean {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    const type = el.type;
    return (
      type === 'text' ||
      type === 'search' ||
      type === 'email' ||
      type === 'password' ||
      type === 'tel' ||
      type === 'url' ||
      type === 'number' ||
      type === ''
    );
  }
  return el instanceof Element && el.getAttribute('contenteditable') === 'true';
}

function viewportMeta(): HTMLMetaElement | null {
  const el = document.querySelector('meta[name="viewport"]');
  return el instanceof HTMLMetaElement ? el : null;
}

/**
 * Stops double-tap and pinch zoom on the full-screen play, match, and event
 * shells, pulls a stuck visual viewport back to 1×, and (via the attribute
 * CSS) blocks text selection so labels don't open the iOS Copy menu.
 *
 * Those screens lock document scroll. Score faces also set `touch-action: pan-y`,
 * so once iOS zooms the page a pinch never reaches the browser. An installed
 * PWA has no address-bar control to reset it.
 */
export function useLockMobileViewportScale(active: boolean): void {
  const override = useDevPhoneLockOverride();
  useEffect(() => {
    if (!active) return undefined;
    const matchMedia =
      typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : undefined;
    const allowed =
      override === 'on' ||
      (override !== 'off' && deviceAllowsViewportScaleLock(matchMedia, navigator));
    if (!allowed) return undefined;

    ensureViewportLockStyles();

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlTouchAction = html.style.touchAction;
    const previousBodyTouchAction = body.style.touchAction;
    html.setAttribute(VIEWPORT_SCALE_LOCK_ATTR, '');
    html.style.touchAction = 'pan-x pan-y';
    body.style.touchAction = 'pan-x pan-y';

    const meta = viewportMeta();
    const previousContent = meta?.getAttribute('content') ?? null;
    if (meta) meta.setAttribute('content', LOCKED_VIEWPORT_CONTENT);

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });

    let disposed = false;
    let snapTimer = 0;
    let snapAttempts = 0;

    const snapIfZoomed = () => {
      if (disposed || !meta) return;
      const scale = window.visualViewport?.scale ?? 1;
      if (scale <= 1.01) {
        snapAttempts = 0;
        return;
      }
      if (isTextField(document.activeElement)) return;
      if (snapTimer || snapAttempts >= MAX_SNAP_ATTEMPTS) return;
      snapAttempts += 1;
      meta.setAttribute('content', OPEN_VIEWPORT_CONTENT);
      snapTimer = window.setTimeout(() => {
        snapTimer = 0;
        if (disposed) return;
        meta.setAttribute('content', LOCKED_VIEWPORT_CONTENT);
      }, SNAP_DELAY_MS);
    };

    let textEntryTimer = 0;
    const snapAfterTextEntry = () => {
      window.clearTimeout(textEntryTimer);
      textEntryTimer = window.setTimeout(snapIfZoomed, SNAP_DELAY_MS);
    };

    window.visualViewport?.addEventListener('resize', snapIfZoomed);
    document.addEventListener('focusout', snapAfterTextEntry);
    snapIfZoomed();

    return () => {
      disposed = true;
      window.clearTimeout(snapTimer);
      window.clearTimeout(textEntryTimer);
      html.removeAttribute(VIEWPORT_SCALE_LOCK_ATTR);
      html.style.touchAction = previousHtmlTouchAction;
      body.style.touchAction = previousBodyTouchAction;
      if (meta && previousContent != null) meta.setAttribute('content', previousContent);
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      window.visualViewport?.removeEventListener('resize', snapIfZoomed);
      document.removeEventListener('focusout', snapAfterTextEntry);
    };
  }, [active, override]);
}
